import { memo, useEffect, useRef, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

import { Stepper } from "@mui/material";

import { Logo } from "@zenith/shared";
import { AppStepper, AppNavigation } from "@zenith/hooks";
import { getCopyright } from "@zenith/utils";
import {
  ApiResponseDTODiscardUpgradeResponseDTO,
  StartUpgradeTierResponseDTO,
  TierApiService,
  TierStepsResponseDTO,
} from "@zenith/services/user";
import { END_UPGRADE_PAGE, UPGRADE_ACCOUNT } from "@zenith/routes/paths";
import { DOCUMENTS_JOB_ID, SELFIE_JOB_ID } from "@zenith/data/constants";
import { useUpgradeContext } from "@zenith/context";
import { ModalComponent } from "@zenith/components";
import {
  outletForTierGold,
  outletForTierPlatinum,
  outletForTierSilver,
  stepTierGold,
  stepTierPlatinum,
  stepTierSilver,
} from "./tiers-helper";
import { AccountTier } from "./AccountTier";
import WalletIcon from "@zenith/assets/icons/WalletIcon";

const TiersLayout = memo(function TiersLayout() {
  const { navigateTo } = AppNavigation();
  const { activeStep, setMaxStep, setActiveStep } = AppStepper();
  const location = useLocation();
  const navType = useNavigationType();
  const {
    upgradeContext: { tierSteps, tierType },
    clearUpgradeContext,
  } = useUpgradeContext();

  const [tier, setTier] = useState<AccountTier>();
  const [discardModal, setDiscardModal] = useState(false);
  const initialStepSetRef = useRef(false);

  // Intercept the browser back button and show the discard modal instead
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      setDiscardModal(true);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handleDiscardUpgrade = () => {
    TierApiService.discardUpgradeTier()
      .then((response: ApiResponseDTODiscardUpgradeResponseDTO) => {
        if ("data" in response && response.data) {
          setActiveStep(0);
          clearUpgradeContext();
          navigateTo(UPGRADE_ACCOUNT);
        }
      })
      .catch((error) => console.error(error));
  };

  useEffect(() => {
    if (navType === "PUSH") {
      initialStepSetRef.current = false;
      setActiveStep(0);
      sessionStorage.removeItem(SELFIE_JOB_ID);
      sessionStorage.removeItem(DOCUMENTS_JOB_ID);
    }
  }, [setActiveStep, navType]);

  // Determine initial active step from tier steps data — runs only once per navigation
  useEffect(() => {
    if (!tierType || !tierSteps) return;
    if (initialStepSetRef.current) return;

    const STEPS = tierSteps as TierStepsResponseDTO[];

    const firstRejectedStep = STEPS.find(
      (step) => step.stepStatus === TierStepsResponseDTO.stepStatus.REJECTED
    );

    const firstNotStartedStep = STEPS.find(
      (step) => step.stepStatus === TierStepsResponseDTO.stepStatus.NOT_STARTED
    );

    initialStepSetRef.current = true;

    if (firstRejectedStep && firstRejectedStep.stepNumber !== undefined) {
      setActiveStep(firstRejectedStep.stepNumber - 1);
    } else if (
      firstNotStartedStep &&
      firstNotStartedStep.stepNumber !== undefined
    ) {
      setActiveStep(firstNotStartedStep.stepNumber - 1);
    } else {
      navigateTo(END_UPGRADE_PAGE);
    }
  }, [tierType, tierSteps, navigateTo, setActiveStep]);

  // Build the tier outlet — re-runs when activeStep changes so each step renders the correct page
  useEffect(() => {
    if (!tierType || !tierSteps) return;

    const TIER = (
      tierType as StartUpgradeTierResponseDTO.tierType
    ).toUpperCase();
    const STEPS = tierSteps as TierStepsResponseDTO[];

    if (activeStep >= STEPS.length) return;

    const tierComponents: {
      [key: string]: {
        stepper: (steps: Array<TierStepsResponseDTO>) => JSX.Element[];
        outlet: (
          activeStep: number,
          tier: string,
          steps: Array<TierStepsResponseDTO>
        ) => JSX.Element;
      };
    } = {
      SILVER: { stepper: stepTierSilver, outlet: outletForTierSilver },
      GOLD: { stepper: stepTierGold, outlet: outletForTierGold },
      PLATINUM: { stepper: stepTierPlatinum, outlet: outletForTierPlatinum },
    };

    const tierInfo = tierComponents[TIER];

    if (tierInfo) {
      setMaxStep(STEPS.length);
      const accountTier = new AccountTier(
        TIER,
        tierInfo.stepper(STEPS),
        tierInfo.outlet(activeStep, TIER, STEPS)
      );
      setTier(accountTier);
    } else {
      console.warn("Tier not found");
    }
  }, [setMaxStep, activeStep, tierType, tierSteps]);

  useEffect(() => {
    if (!tierType || !tierSteps) {
      navigateTo(UPGRADE_ACCOUNT);
      return;
    }

    if (activeStep >= tierSteps.length) {
      navigateTo(END_UPGRADE_PAGE);
      return;
    }
  }, [activeStep, tierSteps, tierType, navigateTo]);

  return (
    <div className="flex min-h-screen w-full">
      <ModalComponent
        title="Discard Upgrade"
        body="Are you sure you want to leave?"
        secondLine="All information provided will be lost."
        showModal={discardModal}
        onClickClose={() => setDiscardModal(false)}
        doubleOptions={true}
        onLeftButtonClick={handleDiscardUpgrade}
        onRightButtonClick={() => setDiscardModal(false)}
        leftButtonText="Discard Upgrade"
        rightButtonText="Return to Upgrade"
      />
      <div className="relative hidden w-80 flex-col items-start justify-center outline outline-1 outline-stone-300 lg:flex">
        <div className="m-12 flex grow flex-col ">
          <div className="flex gap-2">
            <WalletIcon height={40} width={40} className="my-auto" />
            <Logo />
          </div>
          <div className="mt-40 flex w-full grow items-start">
            <Stepper
              activeStep={activeStep}
              orientation="vertical"
              connector={null}
              sx={{
                ".Mui-disabled .StepIcon-circle": {
                  border: "1px solid white",
                  marginTop: "32px",
                },
              }}
            >
              {tier?.tierStepperComponent}
            </Stepper>
          </div>
          <div className="text-gray-300">{getCopyright()}</div>
        </div>
      </div>
      <div
        className="
        flex h-full w-full flex-col items-center justify-center
        px-4 md:px-10 lg:mx-20  lg:w-auto lg:items-start"
      >
        <div className=" mt-4 flex gap-2 lg:hidden">
          <WalletIcon height={40} width={40} className="my-auto" />
          <Logo />
        </div>
        <div className="my-auto flex min-h-[calc(100vh-50px)] w-full flex-col items-center justify-center md:w-1/2 lg:w-full lg:items-start xs:w-3/4">
          {tier?.tierStepperOutlet}
        </div>
      </div>
    </div>
  );
});

export default TiersLayout;
